import React, { useState, useEffect, useMemo, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/admin/institutionForm.scss";
import DropdownSelect from "../common/DropdownSelect";

const InstitutionForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  preferenceList,
}) => {
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  const errorFieldOrder = ["name", "type", "mobileNumber", "website_url"];

  const isValidWebsiteUrl = (value) => {
    try {
      const url = new URL(value);
      return (
        ["http:", "https:"].includes(url.protocol) &&
        url.hostname.includes(".") &&
        !/\s/.test(value)
      );
    } catch {
      return false;
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    board: "",
    description: "",
    mobileNumber: "",
    website_url: "",
    address: "",
    type: {},
    preference_question: {},
    adminPermissions: [
      { permission_type: "course", read: true, write: false },
      { permission_type: "batches", read: true, write: false },
      { permission_type: "teacher", read: true, write: false },
      { permission_type: "student", read: true, write: false },
      { permission_type: "questionbank", read: true, write: false },
      { permission_type: "material", read: true, write: false },
    ],
    teacherPermissions: [
      { permission_type: "course", read: true, write: false },
      { permission_type: "batches", read: true, write: false },
      { permission_type: "student", read: true, write: false },
      { permission_type: "questionbank", read: true, write: false },
      { permission_type: "material", read: true, write: false },
    ],
    disabled: false,
  });

  const ADMIN_PERMISSION_TYPES = [
    "course",
    "batches",
    "teacher",
    "student",
    "questionbank",
    "material",
  ];

  const TEACHER_PERMISSION_TYPES = [
    "course",
    "batches",
    "student",
    "questionbank",
    "material",
  ];

  const preferenceValue = useMemo(
    () =>
      preferenceList?.map((d) => {
        const data = {
          value: d?.id,
          label: d?.name,
        };
        return data;
      }),
    [preferenceList],
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      if (parent === "adminPermissions" && child.includes("[")) {
        const match = child.match(/\[(.+)\]\.(read|write)/);
        if (match) {
          const permType = match[1];
          const key = match[2];
          setFormData((prev) => ({
            ...prev,
            adminPermissions: prev.adminPermissions.map((p) =>
              p.permission_type === permType ? { ...p, [key]: checked } : p,
            ),
          }));
          return;
        }
      }
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  const scrollToFirstError = (newErrors) => {
    const firstErrorKey = errorFieldOrder.find((key) => newErrors[key]);
    if (!firstErrorKey) return;

    requestAnimationFrame(() => {
      const field = formRef.current?.querySelector(
        `[data-error-field="${firstErrorKey}"]`,
      );
      if (!field) return;

      field.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = field.querySelector("input, textarea, select, button");
      input?.focus({ preventScroll: true });
    });
  };

  const handleTeacherPermissionToggle = (permission_type, key, checked) => {
    setFormData((prev) => ({
      ...prev,
      teacherPermissions: prev.teacherPermissions.map((p) =>
        p.permission_type === permission_type ? { ...p, [key]: checked } : p,
      ),
    }));
  };

  const handleAdminPermissionToggle = (permission_type, key, checked) => {
    setFormData((prev) => ({
      ...prev,
      adminPermissions: prev.adminPermissions.map((p) =>
        p.permission_type === permission_type ? { ...p, [key]: checked } : p,
      ),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Institution name is required";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[0-9\s+\-()]{10,20}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid mobile number";
    }

    const websiteUrl = formData.website_url.trim();
    if (websiteUrl && !isValidWebsiteUrl(websiteUrl)) {
      newErrors.website_url =
        "Please enter a valid website URL starting with http:// or https://";
    }

    if (!formData?.type || Object.keys(formData?.type).length === 0) {
      newErrors.type = "Please select the institution type";
    }

    setErrors(newErrors);
    scrollToFirstError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const payload = {
        name: formData.name.trim(),
        board: formData.board.trim(),
        description: formData.description.trim(),
        mobile: formData.mobileNumber.trim(),
        website_url: formData.website_url.trim(),
        address: formData.address.trim(),
        admin_permissions: formData.adminPermissions.map((p) => ({
          permission_type: p.permission_type,
          read: !!p.read,
          write: !!p.write,
        })),
        type: formData?.type?.value,
        preference_question: formData?.preference_question?.value,
        teacher_permissions: formData.teacherPermissions.map((p) => ({
          permission_type: p.permission_type,
          read: !!p.read,
          write: !!p.write,
        })),
        disabled: !!formData.disabled,
      };
      onSubmit(payload);
    }
  };

  useEffect(() => {
    if (initialData) {
      let mappedAdminPerms = [
        { permission_type: "course", read: true, write: false },
        { permission_type: "batches", read: true, write: false },
        { permission_type: "teacher", read: true, write: false },
        { permission_type: "student", read: true, write: false },
        { permission_type: "questionbank", read: true, write: false },
        { permission_type: "material", read: true, write: false },
      ];

      const incomingArray =
        initialData.admin_permissions || initialData.adminPermissions;
      if (Array.isArray(incomingArray)) {
        mappedAdminPerms = mappedAdminPerms.map((def) => {
          const found = incomingArray.find(
            (p) => p.permission_type === def.permission_type,
          );
          return found
            ? {
                permission_type: def.permission_type,
                read: !!found.read,
                write: !!found.write,
              }
            : def;
        });
      } else if (
        initialData.adminPermissions &&
        typeof initialData.adminPermissions === "object"
      ) {
        mappedAdminPerms = mappedAdminPerms.map((def) => ({
          ...def,
          read: !!initialData.adminPermissions.read,
          write: !!initialData.adminPermissions.write,
        }));
      }

      let mappedTeacherPerms = [
        { permission_type: "course", read: true, write: false },
        { permission_type: "batches", read: true, write: false },
        { permission_type: "student", read: true, write: false },
        { permission_type: "questionbank", read: true, write: false },
        { permission_type: "material", read: true, write: false },
      ];

      const incomingTeacherArray =
        initialData.teacher_permissions || initialData.teacherPermissions;
      if (Array.isArray(incomingTeacherArray)) {
        mappedTeacherPerms = mappedTeacherPerms.map((def) => {
          const found = incomingTeacherArray.find(
            (p) => p.permission_type === def.permission_type,
          );
          return found
            ? {
                permission_type: def.permission_type,
                read: !!found.read,
                write: !!found.write,
              }
            : def;
        });
      } else if (
        initialData.teacherPermissions &&
        typeof initialData.teacherPermissions === "object"
      ) {
        mappedTeacherPerms = mappedTeacherPerms.map((def) => ({
          ...def,
          read: !!initialData.teacherPermissions.read,
          write: !!initialData.teacherPermissions.write,
        }));
      }

      setFormData((prev) => ({
        ...prev,
        name: initialData.name || "",
        board: initialData.board || "",
        description: initialData.description || "",
        mobileNumber: initialData.mobile || "",
        website_url: initialData.website_url || "",
        address: initialData.address || "",
        adminPermissions: mappedAdminPerms,
        teacherPermissions: mappedTeacherPerms,
        type:
          initialData?.type === "school"
            ? { value: "school", label: "School" }
            : { value: "college", label: "College" },
        preference_question: preferenceValue?.find(
          (i) => i?.value === initialData?.preference_question_id,
        ),
        disabled:
          typeof initialData.disabled === "boolean"
            ? initialData.disabled
            : !!initialData.isDisabled,
      }));
    } else {
      setFormData({
        name: "",
        board: "",
        description: "",
        mobileNumber: "",
        website_url: "",
        address: "",
        type: {},
        preference_question: {},
        adminPermissions: [
          { permission_type: "course", read: true, write: false },
          { permission_type: "batches", read: true, write: false },
          { permission_type: "teacher", read: true, write: false },
          { permission_type: "student", read: true, write: false },
          { permission_type: "questionbank", read: true, write: false },
          { permission_type: "material", read: true, write: false },
        ],
        teacherPermissions: [
          { permission_type: "course", read: true, write: false },
          { permission_type: "batches", read: true, write: false },
          { permission_type: "student", read: true, write: false },
          { permission_type: "questionbank", read: true, write: false },
          { permission_type: "material", read: true, write: false },
        ],
        disabled: false,
      });
    }
  }, [initialData, isOpen, preferenceValue]);

  return (
    <div className="institution-form-overlay">
      <div className="institution-form-container">
        <div className="form-header">
          <h2>{initialData ? "Edit" : "Create"} Institution</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="institution-form"
          noValidate
        >
          <div
            className={`institution-form-group ${
              errors.name ? "has-error" : ""
            }`}
            data-error-field="name"
          >
            <label htmlFor="name">Institution Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter institution name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
            />
            {errors.name && (
              <span id="name-error" className="error-message">
                {errors.name}
              </span>
            )}
          </div>
          <div className="institution-form-group">
            <label htmlFor="board">Board</label>
            <input
              id="board"
              name="board"
              type="text"
              value={formData.board}
              onChange={handleChange}
              placeholder="Enter board (e.g. CBSE, ICSE)"
            />
          </div>
          <div
            className={`institution-form-dropdown-container ${
              errors.type ? "has-error" : ""
            }`}
            data-error-field="type"
          >
            <label>Institution Type</label>
            <DropdownSelect
              options={[
                { value: "school", label: "School" },
                { value: "college", label: "College" },
              ]}
              value={formData?.type}
              onChange={(vals) => {
                setFormData((prev) => ({
                  ...prev,
                  type: vals,
                }));
                setErrors((prev) => ({ ...prev, type: undefined }));
              }}
              placeholder="Select type"
              searchable
            />
            {errors?.type && (
              <span id="type-error" className="error-message">
                {errors?.type}
              </span>
            )}
          </div>
          <div className=" institution-form-dropdown-container">
            <label>Preference</label>
            <DropdownSelect
              options={preferenceValue}
              value={formData?.preference_question}
              onChange={(vals) => {
                setFormData((prev) => ({
                  ...prev,
                  preference_question: vals,
                }));
              }}
              placeholder="Select Preference"
              searchable
            />
          </div>
          <div className="institution-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter institution description"
              rows="3"
            />
          </div>

          <div
            className={`institution-form-group ${
              errors.mobileNumber ? "has-error" : ""
            }`}
            data-error-field="mobileNumber"
          >
            <label htmlFor="mobileNumber">Mobile Number</label>
            <input
              id="mobileNumber"
              name="mobileNumber"
              type="tel"
              value={formData.mobileNumber}
              onChange={handleChange}
              placeholder="Enter mobile number with country code"
              aria-invalid={!!errors.mobileNumber}
              aria-describedby={
                errors.mobileNumber ? "mobile-error" : undefined
              }
            />
            {errors.mobileNumber && (
              <span id="mobile-error" className="error-message">
                {errors.mobileNumber}
              </span>
            )}
          </div>

          <div
            className={`institution-form-group ${
              errors.website_url ? "has-error" : ""
            }`}
            data-error-field="website_url"
          >
            <label htmlFor="website_url">Website URL</label>
            <input
              id="website_url"
              name="website_url"
              type="url"
              value={formData.website_url}
              onChange={handleChange}
              placeholder="https://example.com"
              aria-invalid={!!errors.website_url}
              aria-describedby={
                errors.website_url ? "website-url-error" : undefined
              }
            />
            {errors.website_url && (
              <span id="website-url-error" className="error-message">
                {errors.website_url}
              </span>
            )}
          </div>

          <div className="institution-form-group">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter address"
              rows="2"
            />
          </div>

          <div className="permissions-section">
            <h4>Admin Permissions</h4>
            <div className="inst-permissions-matrix inst-permissions-matrix--admin">
              <div className="inst-matrix-header">
                <span className="inst-matrix-col inst-type-col inst-matrix-header-info">
                  Module
                </span>
                <span className="inst-matrix-col inst-matrix-header-info text-align">
                  Edit
                </span>
                <span className="inst-matrix-col inst-matrix-header-info text-align">
                  View
                </span>
              </div>
              {ADMIN_PERMISSION_TYPES.map((type) => {
                const entry = formData.adminPermissions.find(
                  (p) => p.permission_type === type,
                );
                return (
                  <div key={type} className="inst-matrix-row">
                    <span className="inst-matrix-col inst-type-col">
                      {type}
                    </span>
                    <span className="inst-matrix-col">
                      <input
                        type="checkbox"
                        name={`adminPermissions[${type}].read`}
                        checked={!!entry?.read}
                        onChange={(e) =>
                          handleAdminPermissionToggle(
                            type,
                            "read",
                            e.target.checked,
                          )
                        }
                      />
                    </span>
                    <span className="inst-matrix-col">
                      <input
                        type="checkbox"
                        name={`adminPermissions[${type}].write`}
                        checked={!!entry?.write}
                        onChange={(e) =>
                          handleAdminPermissionToggle(
                            type,
                            "write",
                            e.target.checked,
                          )
                        }
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {errors.permissions && (
              <span className="error-message">{errors.permissions}</span>
            )}
          </div>

          <div className="permissions-section">
            <h4>Teacher Permissions</h4>
            <div className="inst-permissions-matrix inst-permissions-matrix--teacher">
              <div className="inst-matrix-header">
                <span className="inst-matrix-col inst-type-col inst-matrix-header-info">
                  Module
                </span>
                <span className="inst-matrix-col inst-matrix-header-info text-align">
                  Edit
                </span>
                <span className="inst-matrix-col inst-matrix-header-info text-align">
                  View
                </span>
              </div>
              {TEACHER_PERMISSION_TYPES.map((type) => {
                const entry = formData.teacherPermissions.find(
                  (p) => p.permission_type === type,
                );
                return (
                  <div key={type} className="inst-matrix-row">
                    <span className="inst-matrix-col inst-type-col">
                      {type}
                    </span>
                    <span className="inst-matrix-col">
                      <input
                        type="checkbox"
                        name={`teacherPermissions[${type}].read`}
                        checked={!!entry?.read}
                        onChange={(e) =>
                          handleTeacherPermissionToggle(
                            type,
                            "read",
                            e.target.checked,
                          )
                        }
                      />
                    </span>
                    <span className="inst-matrix-col">
                      <input
                        type="checkbox"
                        name={`teacherPermissions[${type}].write`}
                        checked={!!entry?.write}
                        onChange={(e) =>
                          handleTeacherPermissionToggle(
                            type,
                            "write",
                            e.target.checked,
                          )
                        }
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button " onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-button btn-interactive">
              {initialData ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstitutionForm;
